import {
  Archive,
  Beaker,
  Boxes,
  Check,
  FileCog,
  FileSignature,
  FileStack,
  Ship,
} from "lucide-react"
import type { ComponentType } from "react"
import { useTranslation } from "react-i18next"

import { Card } from "@/components/ui/card"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import {
  OT_STAGES,
  isStageDone,
  isStageUnlocked,
  stageIndex,
} from "@/features/transit/lib/stages"
import { cn } from "@/lib/utils"
import type { OT, OTStage } from "@/features/transit/api/types"

const STAGE_ICONS: Record<OTStage, ComponentType<{ className?: string }>> = {
  CREATED: FileSignature,
  ANALYSE: Beaker,
  FORMULE: FileCog,
  EMPOTAGE: Boxes,
  EMBARQUEMENT: Ship,
  REMISE: FileStack,
  TERMINER: Archive,
}

interface StepperProps {
  ot: OT
  // The stage whose panel is currently visible. Drives the "viewed"
  // highlight, which may differ from the OT's real stage when the user
  // is inspecting a previously-completed step.
  viewedStage?: OTStage
  // Pure panel selector — no API call. Caller updates local state.
  onJump?: (stage: OTStage) => void
}

export function Stepper({ ot, viewedStage, onJump }: StepperProps) {
  const { t } = useTranslation("transit")
  const currentIdx = stageIndex(ot.stage)
  const viewed = viewedStage ?? ot.stage

  return (
    <Card className="p-3">
      <ol className="flex flex-wrap items-stretch gap-2">
        {OT_STAGES.map((stage, i) => {
          const Icon = STAGE_ICONS[stage]
          const done = isStageDone(ot.stage, stage)
          const isCurrentReal = i === currentIdx
          const isViewed = stage === viewed
          const unlocked = isStageUnlocked(ot.unlockedStages, stage)
          const clickable = unlocked && !isViewed && !!onJump

          // Tile palette:
          //   - current OR viewed: primary border + soft primary tint.
          //     The "Current" caption underneath the title disambiguates
          //     when the user is viewing a stage that isn't current.
          //   - done (past): neutral card.
          //   - unlocked future: neutral card.
          //   - locked future: muted.
          const tile =
            isCurrentReal || isViewed
              ? "border-primary/60 bg-primary/5 text-foreground"
              : done
                ? "border-border bg-card text-foreground"
                : unlocked
                  ? "border-border bg-card text-foreground"
                  : "border-border bg-muted/40 text-muted-foreground"

          const ring =
            isCurrentReal || isViewed
              ? "bg-primary text-primary-foreground"
              : done
                ? "bg-emerald-600 text-white"
                : "bg-muted text-muted-foreground"

          const titleClass = "truncate text-xs font-medium"

          return (
            <li key={stage} className="min-w-0 flex-1 basis-32">
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => clickable && onJump?.(stage)}
                    disabled={!clickable}
                    className={cn(
                      "relative flex h-11 w-full items-center gap-2 rounded-md border px-3 py-2 text-left transition-colors",
                      tile,
                      clickable && "cursor-pointer hover:bg-muted/30"
                    )}
                    aria-current={isViewed ? "step" : undefined}
                  >
                    <span
                      className={cn(
                        "flex size-6 flex-shrink-0 items-center justify-center rounded-full",
                        ring
                      )}
                    >
                      {done ? (
                        <Check className="size-3.5" />
                      ) : (
                        <Icon className="size-3.5" />
                      )}
                    </span>
                    {/* Two-line text block on every tile — non-current
                        tiles fill the second line with empty space so
                        heights and vertical centring match. */}
                    <div className="flex min-w-0 flex-1 flex-col justify-center leading-tight">
                      <div className={titleClass}>{t(`stages.${stage}`)}</div>
                      {isCurrentReal ? (
                        <div className="text-[9px] font-semibold tracking-wider text-primary uppercase">
                          {t("stepper.currentBadge")}
                        </div>
                      ) : null}
                    </div>
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  {clickable
                    ? t("stepper.jumpHint", { stage: t(`stages.${stage}`) })
                    : isCurrentReal
                      ? t("stepper.activeHint")
                      : done
                        ? t("stepper.doneHint")
                        : t("stepper.lockedHint")}
                </TooltipContent>
              </Tooltip>
            </li>
          )
        })}
      </ol>
    </Card>
  )
}
